import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import React, { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  TPopulatedForm,
  applicationsKeyStore,
  useApplicationFormSteps,
  useCreateDraft,
  useDeclaration,
  useSubmitApplication,
} from "../../hooks/applications.hooks";
import { useApplicationAccessBlock } from "../../hooks/application-access.hooks";
import { cleanseObject } from "../../utils/common";
import { ErrorToast } from "../toastMessage";
import {
  useSelectedApplication,
  useSetSelectedApplication,
} from "./ApplicationFrom.ctx";
import { RenderNOC } from "./RenderNOC";
import { RenderStep } from "./RenderStep";
import { ApplicationDownloadNote, DownloadFormButton } from "./ApplicationDownloadNote";
import { cookieUtils } from "../../utils/cookieUtils";
import {DuplicateApplicationPopup} from "../DuplicateApplicationPopup";
import {RL_APPLICATION_TYPE} from "../../config/phaseConstants";

type PropTypes = {
  defaultValues: Record<string, unknown>;
  defaultSteps: TPopulatedForm[];
};

export function BottomButtons({
  formState,
  defaults,
  getValues,
  trigger,
  serviceType,
  setDefaults,
  activeStep,
  setActiveStep,
  steps,
}: ReturnType<typeof useForm> & {
  defaults: Record<string, unknown>;
  activeStep: number;
  steps: TPopulatedForm[];
  serviceType?: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setDefaults: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}) {
  const { data: declaration } = useDeclaration();
  const isFetchingSteps =
    useIsFetching({
      queryKey: applicationsKeyStore.serviceFormSteps(serviceType),
      exact: true,
    }) > 0;

  const disableBtns = useIsMutating() > 0;
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const setApplication = useSetSelectedApplication();
  const application = useSelectedApplication();
  const createDraftMutation = useCreateDraft();
  const createDraftMutation1 = useCreateDraft();
  const submitMutation = useSubmitApplication();
  /**
   * An applicant the Admin has barred may still edit and save a draft but
   * cannot submit it; the backend refuses the submit as well.
   */
  const { isBlocked: submitBlocked, notice: submitBlockedNotice } =
    useApplicationAccessBlock();
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  // Check if application has been saved at least once
  // Check if application has been saved at least once
  const hasBeenSaved = !!application?.id;

  if (!serviceType || Number(serviceType) < 0 || isFetchingSteps)
    return null;

  const getCleanData = () => {
    const cleansedObject = cleanseObject(getValues());
    if (cleansedObject.plot_no) {
      cleansedObject.plot_no = String(cleansedObject.plot_no).replace(/^0+(?=\d)/, "");
    }
    return {
      ...defaults,
      ...cleansedObject,
      // 🔒 force system fields into payload
      submittedBy: defaults.submitted_by,
      type_of_application_submitter: defaults.
          type_of_application_submitter,
      rl_application_type:
          cleansedObject.rl_application_type ||
          defaults.rl_application_type ||
          RL_APPLICATION_TYPE.TEMPORARY,
      ...(!!cleansedObject?.documents && {
        documents: {
          ...(defaults.documents as Record<string, unknown>),
          ...(cleansedObject.documents as Record<string, unknown>),
        },
      }),
    };
  };

  const onSave = async () => {
    try {
      const newDefaults = getCleanData();
      setDefaults(newDefaults);
      const result = await createDraftMutation.mutateAsync({
        data: newDefaults, id: application?.id,
      });
      result.data?.data?.applicationId && setApplication(result.data.data);
    } catch (err: any) {
      console.log("[SAVE_DRAFT_UI_ERROR]", err?.response?.status);
      if (err?.response?.status === 409) {
        setShowDuplicatePopup(true);
      }
    }
  };

  const onSubmit = async () => {
    try {
      if (!formState.isValid)
        return trigger(undefined, { shouldFocus: true }).then(() => null);
      const cleanData = getCleanData();
      setDefaults(cleanData);
      const result = await createDraftMutation.mutateAsync({
        data: cleanData,
        id: application?.id,
      });

      if (!result.data?.data?.applicationId) return;
      setApplication(result.data.data);

      const result2 = await submitMutation.mutateAsync({
        id: result.data.data.id,
      });
      if (!result2.data.success) throw new Error();

      return navigate("/user-applications");
    } catch (err) {
      //no-op
    }
  };

  if (activeStep === steps.length - 1) {
    if (!declaration)
      return (
        <div className="flex justify-center space-x-4 items-center mt-8">
          <p className="text-base">
            Cannot submit application! Declaration misconfigured.
          </p>
        </div>
      );
    return (
      <div className="max-w-[1000px] mx-auto mt-8 flex flex-col gap-6">
        <div>
          <p className="font-semibold text-lg text-grey-600 mb-2">Declaration</p>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={checked}
              className="w-auto mr-4 shrink-0 basis-[15px]"
              onChange={(e) => setChecked(e.target.checked)}
            />
            <p className="grow text-base">{declaration}</p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-4 w-full max-w-[650px] mx-auto mt-4">
          {activeStep > 0 && (
            <button
              type="button"
              disabled={disableBtns}
              onClick={() => setActiveStep((e) => e - 1)}
              className="w-full sm:w-auto bg-black px-6 py-2.5 sm:py-2 rounded-full font-bold text-white text-sm sm:text-base"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={disableBtns}
            className="w-full sm:w-auto bg-primary px-6 py-2.5 sm:py-2 rounded-full font-bold text-white text-sm sm:text-base"
          >
            {createDraftMutation.isPending ? "Saving" : "Save"}
          </button>
          {/*
            Before:
            disabled={disableBtns || !checked}
          */}
          <button
            type="button"
            onClick={onSubmit}
            disabled={disableBtns || !checked || submitBlocked}
            title={submitBlockedNotice}
            className="w-full sm:w-auto bg-green-600 px-6 py-2.5 sm:py-2 rounded-full font-bold text-white text-sm sm:text-base disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {submitMutation.isPending ? "Submitting" : "Submit"}
          </button>
        </div>
        {submitBlocked && (
          <p className="text-sm font-semibold text-red-700 text-center mt-3">
            {submitBlockedNotice}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-4 w-full max-w-[650px] mx-auto mt-8">
      {activeStep > 0 && (
        <button
          type="button"
          disabled={disableBtns}
          onClick={() => setActiveStep((e) => e - 1)}
          className="w-full sm:w-auto bg-black px-6 py-2.5 sm:py-2 rounded-full font-bold text-white text-sm sm:text-base"
        >
          Back
        </button>
      )}

      {activeStep === 0 && (
        <DownloadFormButton
          getValues={getValues}
          defaults={defaults}
          /**
           * The real Documents-step config list (cp_form_documents_mappings-
           * driven, same data RenderField.tsx's Documents step renders from)
           * - merged across every step since document configs aren't
           * guaranteed to all live on one step. This is NOT the same list as
           * the "Registration Document" type endpoint, which only covers a
           * different, unrelated document set (e.g. Aadhar Card at
           * registration time).
           */
          documentConfigs={steps.flatMap((s) => s.documentConfigs || [])}
        />
      )}

      <button
        type="button"
        onClick={onSave}
        disabled={disableBtns}
        className="w-full sm:w-auto bg-primary px-6 py-2.5 sm:py-2 rounded-full font-bold text-white text-sm sm:text-base"
      >
        {createDraftMutation.isPending ? "Saving" : "Save as draft"}
      </button>

      <button
        type="button"
        disabled={disableBtns || !hasBeenSaved}
        onClick={() => {
          if (!formState.isValid)
            return trigger(undefined, { shouldFocus: true }).then(() => null);

          setDefaults(getCleanData());
          setActiveStep((e) => e + 1);
        }}
        className="w-full sm:w-auto bg-primary px-6 py-2.5 sm:py-2 rounded-full font-bold text-white text-sm sm:text-base disabled:bg-gray-300 disabled:cursor-not-allowed"
        title={
          !hasBeenSaved
            ? "Please save the application at least once before proceeding"
            : ""
        }
      >
        Next
      </button>
      <DuplicateApplicationPopup
          open={showDuplicatePopup}
          onClose={() => setShowDuplicatePopup(false)}
      />
    </div>
  );
}

export function ApplicationForm({ defaultValues, defaultSteps }: PropTypes) {
  const registrationData =
    cookieUtils.getSecureCookie("userRegistrationDetails");
  const userDetails = cookieUtils.getSecureCookie("userDetails");

  const loggedInUserType = registrationData?.user_type || "";
  const loggedInUser = userDetails?.name || "";
  const [serviceType, setServiceType] = useState(
    Number(defaultValues?.name_of_service) || undefined
  );
  const { data, isError, isFetching } = useApplicationFormSteps(serviceType);
  // const [defaults, setDefaults] = useState({ ...defaultValues });
  const [defaults, setDefaults] = useState<Record<string, unknown>>(
    () => ({
      ...defaultValues,
      typeOfApplicationSubmitter:
        (defaultValues as Record<string, unknown>)?.[
        "typeOfApplicationSubmitter"
        ] ?? loggedInUserType,
      submitted_by:
        (defaultValues as Record<string, unknown>)?.[
        "submitted_by"
        ] ?? loggedInUser,
    })
  );
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(defaultSteps);

  console.log(
    "[AppsetShowDuplicatePopuplicationForm] defaultValues received:",
    defaultValues
  );
  console.log(
    "typeOfApplicationSubmitterBy:", loggedInUser,
  );

  useEffect(() => {
    if (isFetching) return;
    console.log("step", steps);
    if (serviceType && data?.data?.length) setSteps(data.data);
    else if (serviceType && (isError || !data?.data?.length)) {
      setServiceType(undefined);
      setSteps(defaultSteps);
      ErrorToast("Service Unavailable!");
    }
  }, [isError, serviceType, data, isFetching]);

  const renderButtons = (formMethods: ReturnType<typeof useForm>) => (
 <BottomButtons
      {...formMethods}
      steps={steps}
      defaults={defaults}
      activeStep={activeStep}
      serviceType={serviceType}
      setActiveStep={setActiveStep}
      setDefaults={setDefaults}
    />
);
  const isStepOne = activeStep === 0;

  let toRender = null;

  if (steps[activeStep].type === "NOC") {
    toRender = (
      <RenderNOC
        defaultValues={defaults}
        step={steps[activeStep]}
        key={`form-step-${steps[activeStep]?.id}`}
      >
        {renderButtons}
      </RenderNOC>
    );
  } else {
    toRender = (
      <RenderStep
        defaultValues={defaults}
        step={steps[activeStep]}
        key={`form-step-${steps[activeStep]?.id}`}
        onChangeService={setServiceType}
      >
        {/* {renderButtons} */}
        {(formMethods) => (
          <>
            {isStepOne && <ApplicationDownloadNote />}
            {renderButtons(formMethods)}
          </>
        )}
      </RenderStep>
    );
  }

  console.log(
    "[ApplicationForm] Passing defaults to RenderStep:",
    defaults
  );

  return (
    <div className="px-2 pb-2 md:p-4 overflow-auto">
      <div className="bg-white rounded-xl shadow-lg border border-gray-300 p-8">
        <p className="text-center mb-8 text-grey-600 text-2xl font-bold">
          Application Details
        </p>
        <div className="flex justify-center mb-8 max-w-[550px] mx-auto">
          {steps.map((step, i) => (
            <Fragment key={`step-${step.id}`}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-[20px] h-[20px] box-content p-1 rounded-full border-4 ${i <= activeStep ? "border-primary" : "border-gray-400"
                    }`}
                >
                  <div
                    className={`w-[20px] box-content h-[20px] rounded-full ${i <= activeStep ? "bg-primary" : "bg-gray-400"
                      }`}
                  ></div>
                </div>
                <p className="text-base">{`Step ${i + 1}`}</p>
              </div>

              {i !== steps.length - 1 && (
                <hr
                  className={`grow mt-[16px] border-4 ${i < activeStep ? "border-primary" : ""
                    }`}
                />
              )}
            </Fragment>
          ))}
        </div>
        {toRender}
      </div>
    </div>
  );
}
